/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faUsers,
  faBuilding,
  faCalendar,
  faChartBar,
  faCog,
  faCheckCircle,
  faTrophy,
  faArrowUp,
  faArrowDown,
  faUser,
  faUpload,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import ThemedLoadingSpinner from '../components/ThemedLoadingSpinner';
import UniversityDashboardHeader from '../components/UniversityDashboardHeader';
import StatCard from '../components/StatCard';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import '../styles/UniversityDashboard.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const UniversityDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [activeSection, setActiveSection] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState('');
  const [students, setStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentFilters, setStudentFilters] = useState({
    department: '',
    year: '',
    status: ''
  });
  // Settings state (reuses generic /api/user/settings backend)
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [settingsSuccess, setSettingsSuccess] = useState('');
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    placementAlerts: true,
    bulkUploadNotifications: true,
    eventReminders: true,
    weeklyDigest: false
  });
  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: 'private',
    showEmail: false,
    showPhone: false,
    searchableByRecruiters: false,
    showLastActive: false,
    shareStudentProfilesWithEmployers: true,
    allowExternalCompanyAccess: true,
    shareAnalyticsWithAdmin: true
  });
  const [displaySettings, setDisplaySettings] = useState({
    theme: 'light',
    language: 'en',
    timezone: 'Africa/Nairobi'
  });
  // Events state
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState('');
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventFilter, setEventFilter] = useState('all'); // all, upcoming, past
  // Companies state
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesError, setCompaniesError] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    type: 'Campus Drive',
    maxAttendees: '',
    organizer: '',
    contactEmail: '',
    contactPhone: ''
  });
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalStudents: 0,
      activeCompanies: 0,
      upcomingEvents: 0,
      placementRate: 0,
      studentsPlaced: 0,
      activeJobSeekers: 0,
      averageSalary: 0,
      activeInterns: 0
    },
    profileCompletion: 0,
    departments: [],
    recentPlacements: [],
    students: [],
    companies: [],
    events: []
  });

  // Navigation items for University dashboard - dynamically computed with real student count
  const navigationItems = useMemo(() => {
    const totalStudents = dashboardData.stats.totalStudents || 0;
    return [
      { id: 'dashboard', icon: faChartLine, label: 'Dashboard', section: 'dashboard' },
      { 
        id: 'students', 
        icon: faUsers, 
        label: 'Students', 
        section: 'students', 
        badge: totalStudents > 0 ? String(totalStudents) : undefined
      },
      { id: 'bulk-upload', icon: faUpload, label: 'Bulk Upload', section: 'bulk-upload' },
      { id: 'companies', icon: faBuilding, label: 'Companies', section: 'companies' },
      { id: 'events', icon: faCalendar, label: 'Events', section: 'events' },
      { id: 'analytics', icon: faChartBar, label: 'Analytics', section: 'analytics' },
      { id: 'settings', icon: faCog, label: 'Settings', section: 'settings' },
      { id: 'myprofile', icon: faUser, label: 'My Profile', section: 'myprofile' }
    ];
  }, [dashboardData.stats.totalStudents]);

  // Handle navigation click
  const handleNavClick = (item) => {
    if (item.section === 'myprofile') {
      navigate('/university-my-profile');
      return;
    }
    setActiveSection(item.section);
  };

  // Get section title for header
  const getSectionTitle = () => {
    const titleMap = {
      'dashboard': 'Dashboard',
      'students': 'Students',
      'bulk-upload': 'Bulk Upload',
      'companies': 'Companies',
      'events': 'Events',
      'analytics': 'Analytics',
      'settings': 'Settings'
    };
    return titleMap[activeSection] || 'Dashboard';
  };

  // Fetch dashboard data (real stats from backend) - reusable function
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch stats
      const statsResponse = await axios.get(
        buildApiUrl('/api/university/stats'),
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Fetch profile to calculate completion
      let profileCompletion = 0;
      try {
        const profileResponse = await axios.get(
          buildApiUrl('/api/user/profile'),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (profileResponse.data) {
          const profile = profileResponse.data;
          
          // Calculate profile completion based on university-specific fields
          const requiredFields = [
            'universityName',
            'institutionType',
            'registrationNumber',
            'officialEmail',
            'phoneNumber',
            'physicalAddress',
            'city',
            'county',
            'contactPersonName',
            'contactPersonTitle',
            'contactPersonPhone',
            'contactPersonEmail'
          ];
          
          // Count filled required fields
          const filledFields = requiredFields.filter(field => {
            const value = profile[field];
            return value && value.toString().trim() !== '';
          }).length;
          
          // Check for optional but important fields
          const optionalFields = [
            'websiteUrl',
            'totalStudentEnrollment',
            'numberOfPrograms',
            'careerCenterEmail',
            'careerOfficePhone',
            'areasOfSpecialization'
          ];
          
          const filledOptional = optionalFields.filter(field => {
            const value = profile[field];
            if (field === 'areasOfSpecialization') {
              return Array.isArray(value) ? value.length > 0 : (value && value.toString().trim() !== '');
            }
            return value && value.toString().trim() !== '';
          }).length;
          
          // Calculate completion: 70% for required fields, 30% for optional
          const requiredPercentage = (filledFields / requiredFields.length) * 70;
          const optionalPercentage = (filledOptional / optionalFields.length) * 30;
          profileCompletion = Math.round(requiredPercentage + optionalPercentage);
          
          // If profile is marked as completed, ensure it shows 100%
          if (profile.profileCompleted || profile.hasCompletedProfile) {
            profileCompletion = 100;
          }
        }
      } catch (profileError) {
        console.error('Error fetching profile for completion:', profileError);
        // Continue with stats even if profile fetch fails
      }

      if (statsResponse.data?.success) {
        const stats = statsResponse.data.stats || {};
        const departments = statsResponse.data.departments || [];

        setDashboardData(prev => ({
          ...prev,
          stats: {
            ...prev.stats,
            totalStudents: stats.totalStudents || 0,
            activeCompanies: stats.companiesRecruiting || 0,
            upcomingEvents: stats.upcomingEvents || 0,
            placementRate: stats.placementRate || 0,
            studentsPlaced: stats.studentsPlaced || 0,
            activeJobSeekers: stats.activeJobSeekers || 0,
            averageSalary: stats.averageSalary || 0,
            activeInterns: stats.activeInterns || 0
          },
          departments,
          profileCompletion: Math.max(0, Math.min(100, profileCompletion))
        }));
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  }, []);

  // Initial fetch on mount
  // Check if this is Moringa School and redirect to Moringa Dashboard
  useEffect(() => {
    const checkMoringaRedirect = async () => {
      try {
        const token = localStorage.getItem('token');
        const profileResponse = await axios.get(
          buildApiUrl('/api/user/profile'),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (profileResponse.data) {
          const universityName = (profileResponse.data.universityName || '').toLowerCase();
          if (universityName.includes('moringa')) {
            console.log('🔐 University Dashboard - Moringa School detected, redirecting to Moringa Dashboard');
            navigate('/moringa-dashboard', { replace: true });
            return;
          }
        }
      } catch (error) {
        console.error('Error checking university name:', error);
      }
    };
    
    checkMoringaRedirect();
  }, [navigate]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Fetch user-level settings when settings section is active
  useEffect(() => {
    const fetchSettings = async () => {
      if (activeSection !== 'settings') return;

      try {
        setSettingsLoading(true);
        setSettingsError('');
        setSettingsSuccess('');

        const token = localStorage.getItem('token');
        if (!token) {
          setSettingsError('You need to be logged in to view settings.');
          return;
        }

        const response = await axios.get(
          buildApiUrl('/api/user/settings'),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data) {
          // Merge notification settings, preserving university-specific fields
          if (response.data.notifications) {
            setNotificationSettings(prev => ({
              emailNotifications: response.data.notifications.emailNotifications ?? prev.emailNotifications,
              placementAlerts: response.data.notifications.placementAlerts ?? prev.placementAlerts,
              bulkUploadNotifications: response.data.notifications.bulkUploadNotifications ?? prev.bulkUploadNotifications,
              eventReminders: response.data.notifications.eventReminders ?? prev.eventReminders,
              weeklyDigest: response.data.notifications.weeklyDigest ?? prev.weeklyDigest,
              // Also preserve any generic notification fields that might exist
              ...response.data.notifications
            }));
          }
          // Merge privacy settings, preserving university-specific fields
          if (response.data.privacy) {
            setPrivacySettings(prev => ({
              ...prev,
              shareStudentProfilesWithEmployers: response.data.privacy.shareStudentProfilesWithEmployers ?? prev.shareStudentProfilesWithEmployers,
              allowExternalCompanyAccess: response.data.privacy.allowExternalCompanyAccess ?? prev.allowExternalCompanyAccess,
              shareAnalyticsWithAdmin: response.data.privacy.shareAnalyticsWithAdmin ?? prev.shareAnalyticsWithAdmin,
              // Also preserve any generic privacy fields
              ...response.data.privacy
            }));
          }
          // Merge display settings
          if (response.data.display) {
            setDisplaySettings(prev => ({
              theme: response.data.display.theme ?? prev.theme,
              language: response.data.display.language ?? prev.language,
              timezone: response.data.display.timezone ?? prev.timezone
            }));
          }
        }
      } catch (error) {
        console.error('Failed to load university settings:', error);
        // Don't show error if it's just that settings don't exist yet (404)
        if (error.response?.status !== 404) {
          setSettingsError('Unable to load settings. Using default values.');
        }
      } finally {
        setSettingsLoading(false);
      }
    };

    fetchSettings();
  }, [activeSection]);

  // Fetch events when Events section is opened
  useEffect(() => {
    const fetchEvents = async () => {
      if (activeSection !== 'events') return;

      try {
        setEventsLoading(true);
        setEventsError('');
        const token = localStorage.getItem('token');
        const response = await axios.get(
          buildApiUrl('/api/university/events'),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.success) {
          setEvents(response.data.events || []);
        }
      } catch (error) {
        console.error('Failed to fetch events:', error);
        setEventsError('Failed to load events.');
      } finally {
        setEventsLoading(false);
      }
    };

    fetchEvents();
  }, [activeSection]);

  // Fetch companies when Companies section is opened
  useEffect(() => {
    const fetchCompanies = async () => {
      if (activeSection !== 'companies') return;

      try {
        setCompaniesLoading(true);
        setCompaniesError('');
        const token = localStorage.getItem('token');
        const response = await axios.get(
          buildApiUrl('/api/university/companies'),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.success) {
          setCompanies(response.data.companies || []);
        }
      } catch (error) {
        console.error('Failed to fetch companies:', error);
        setCompaniesError('Failed to load companies.');
      } finally {
        setCompaniesLoading(false);
      }
    };

    fetchCompanies();
  }, [activeSection]);

  // Fetch students - reusable function
  const fetchStudents = useCallback(async (forceRefresh = false) => {
    // Only fetch when on students section, unless force refresh is requested
    if (!forceRefresh && activeSection !== 'students') return;

    try {
      setStudentsLoading(true);
      setStudentsError('');

      const token = localStorage.getItem('token');
      const params = {};
      if (studentFilters.department) params.department = studentFilters.department;
      if (studentFilters.year) params.year = studentFilters.year;
      if (studentFilters.status) params.status = studentFilters.status;

      const response = await axios.get(
        buildApiUrl('/api/university/students'),
        {
          headers: { Authorization: `Bearer ${token}` },
          params
        }
      );

      if (response.data?.success) {
        setStudents(response.data.students || []);
      } else {
        setStudents([]);
        setStudentsError('Unable to load students right now.');
      }
    } catch (error) {
      console.error('Error fetching university students:', error);
      setStudents([]);
      setStudentsError(error.response?.data?.error || 'Failed to fetch students.');
    } finally {
      setStudentsLoading(false);
    }
  }, [activeSection, studentFilters.department, studentFilters.year, studentFilters.status]);

  // Fetch students when Students section is opened or filters change
  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // Render Dashboard section
  const renderDashboard = () => (
    <div style={{ marginTop: '-1rem', paddingTop: '0' }}>
      {/* Stats Grid */}
      <div className="stats-grid">
        <StatCard
          title="Total Students"
          value={dashboardData.stats.totalStudents}
          trend={-12}
          trendValue=""
          trendLabel="last week"
          icon={faUsers}
          iconColor="#f97316"
        />
        <StatCard
          title="Active Companies"
          value={dashboardData.stats.activeCompanies}
          trend={0}
          trendValue=""
          trendLabel=""
          icon={faBuilding}
          iconColor="#0d9488"
        />
        <StatCard
          title="Upcoming Events"
          value={dashboardData.stats.upcomingEvents}
          trend={8}
          trendValue=""
          trendLabel="last week"
          icon={faCalendar}
          iconColor="#3b82f6"
        />
        <StatCard
          title="Placement Rate"
          value={`${dashboardData.stats.placementRate}%`}
          trend={5}
          trendValue=""
          trendLabel="this month"
          icon={faTrophy}
          iconColor="#10b981"
        />
      </div>

      {/* Department-wise Placement Performance */}
      <div className="section-card">
        <h2><FontAwesomeIcon icon={faChartBar} /> Department-wise Placement Performance</h2>
        <div className="departments-list">
          {dashboardData.departments.map((dept, idx) => (
            <div key={idx} className="department-item">
              <div className="department-header">
                <h4>{dept.name}</h4>
                <span className="placement-rate">{dept.rate}%</span>
              </div>
              <div className="progress-bar-container">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${dept.rate}%` }}></div>
                </div>
                <span className="progress-label">{dept.rate}% Placement Rate</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Placements */}
      <div className="section-card">
        <h2><FontAwesomeIcon icon={faTrophy} /> Recent Placements</h2>
        <div className="placements-list">
          {dashboardData.recentPlacements.slice(0, 5).map((student, idx) => (
            <div key={idx} className="placement-item">
              <div className="placement-avatar">
                {student.name.charAt(0)}
              </div>
              <div className="placement-info">
                <h4>{student.name}</h4>
                <p>{student.department} • {student.year}</p>
              </div>
              <div className="placement-badge">
                <FontAwesomeIcon icon={faCheckCircle} /> Placed
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Render other sections
  const handleBulkStudentsUpload = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    // Use a simple, human-friendly label so Super Admin can identify the batch
    const defaultLabel = `Cohort Upload - ${new Date().getFullYear()}`;
    formData.append('file', file);
    formData.append('cohortLabel', defaultLabel);

    try {
      setBulkUploading(true);
      setBulkUploadResult(null);

      const token = localStorage.getItem('token');
      const response = await axios.post(
        buildApiUrl('/api/university/bulk-students/upload'),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setBulkUploadResult({
        type: 'success',
        message: response.data?.message || 'Student file uploaded successfully.'
      });
      // Reset the input so the same file can be selected again if needed
      event.target.value = '';
      
      // Refresh dashboard stats and students list to show newly created students
      await fetchDashboardData();
      // Refresh the students list (force refresh even if not on students section)
      await fetchStudents(true);
    } catch (error) {
      console.error('Bulk student upload failed:', error);
      setBulkUploadResult({
        type: 'error',
        message: error.response?.data?.error || error.message || 'Upload failed'
      });
    } finally {
      setBulkUploading(false);
    }
  };

  const handleDownloadStudentTemplate = () => {
    // Reuse existing intern template for university student uploads
    const url = buildApiUrl('/api/bulk-import/template/intern');
    window.open(url, '_blank', 'noopener');
  };

  const renderStudents = () => {
    // Derive quick stats from current students list
    const total = students.length;
    const placed = students.filter(s => (s.status || '').toLowerCase() === 'placed').length;
    const activeSeekers = students.filter(s => {
      const status = (s.status || '').toLowerCase();
      return ['applied', 'interviewing'].includes(status);
    }).length;
    const interns = students.filter(s => (s.userType || '').toLowerCase() === 'intern').length;

    // Faculty-wise (department-wise) distribution
    const facultyMap = new Map();
    students.forEach((s) => {
      const facultyKey = (s.faculty || s.department || 'Not specified') || 'Not specified';
      if (!facultyMap.has(facultyKey)) {
        facultyMap.set(facultyKey, {
          name: facultyKey,
          total: 0,
          placed: 0,
          active: 0
        });
      }
      const entry = facultyMap.get(facultyKey);
      entry.total += 1;
      const status = (s.status || '').toLowerCase();
      if (status === 'placed') entry.placed += 1;
      if (['applied', 'interviewing'].includes(status)) entry.active += 1;
    });
    const facultyStats = Array.from(facultyMap.values()).sort((a, b) => b.total - a.total);

    const uniqueDepartments = Array.from(
      new Set(students.map(s => s.department).filter(Boolean))
    );
    const uniqueYears = Array.from(
      new Set(students.map(s => s.year).filter(Boolean))
    );

    const filteredStudents = students.filter((student) => {
      if (!studentSearch.trim()) return true;
      const q = studentSearch.toLowerCase();
      return (
        (student.name || '').toLowerCase().includes(q) ||
        (student.email || '').toLowerCase().includes(q) ||
        (student.department || '').toLowerCase().includes(q)
      );
    });

    const getStatusClass = (status) => {
      const value = (status || '').toLowerCase();
      if (value === 'placed') return 'status-badge status-offered';
      if (value === 'interviewing') return 'status-badge status-interview';
      if (value === 'applied') return 'status-badge status-applied';
      return 'status-badge status-pending';
    };

    return (
    <div className="section-card">
      <h2>Students Management</h2>
        <p style={{ marginBottom: '1.5rem', color: '#64748b', maxWidth: '720px' }}>
          View and manage all your students and interns in one place. Track placement status
          and monitor applications and readiness scores.
        </p>

        {/* Quick stats row (without Active Job Seekers card) */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}
        >
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
              Total Students
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#0f172a' }}>{total}</div>
          </div>
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
              Placed
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#10b981' }}>{placed}</div>
          </div>
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
              Active Interns
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#3b82f6' }}>{interns}</div>
          </div>
        </div>

        {/* Faculty-wise distribution - moved up just after stats */}
        <div
          className="section-card"
          style={{
            padding: '1.5rem 1.75rem',
            marginBottom: '1.5rem'
          }}
        >
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: '#0f172a' }}>
            Faculty-wise distribution
          </h3>
          {facultyStats.length === 0 ? (
            <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0 }}>
              Once students are added, you’ll see a breakdown by faculty/department here.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {facultyStats.map((faculty) => {
                const placedRate =
                  faculty.total > 0 ? Math.round((faculty.placed / faculty.total) * 100) : 0;
                return (
                  <div
                    key={faculty.name}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}
                  >
                    <div style={{ minWidth: '200px' }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#0f172a' }}>
                        {faculty.name}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                        {faculty.total} students • {faculty.placed} placed • {faculty.active} active
                      </div>
                    </div>
                    <div
                      style={{
                        flex: 1,
                        height: '10px',
                        borderRadius: '999px',
                        background: '#e5e7eb',
                        overflow: 'hidden'
                      }}
                    >
                      <div
                        style={{
                          width: `${placedRate}%`,
                          height: '100%',
                          background:
                            'linear-gradient(90deg, rgba(16,185,129,1) 0%, rgba(59,130,246,1) 100%)',
                          transition: 'width 0.4s ease'
                        }}
                      />
                    </div>
                    <div
                      style={{
                        width: '52px',
                        textAlign: 'right',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: '#0f172a'
                      }}
                    >
                      {placedRate}%
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Filters + search + bulk upload actions */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '1rem',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            marginBottom: '1.25rem'
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: '#6b7280',
                  marginBottom: '0.25rem'
                }}
              >
                Department
              </label>
              <select
                className="filter-select"
                value={studentFilters.department}
                onChange={(e) => setStudentFilters(prev => ({ ...prev, department: e.target.value }))}
                style={{ minWidth: '160px' }}
              >
                <option value="">All</option>
                {uniqueDepartments.map((dept) => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: '#6b7280',
                  marginBottom: '0.25rem'
                }}
              >
                Year
              </label>
              <select
                className="filter-select"
                value={studentFilters.year}
                onChange={(e) => setStudentFilters(prev => ({ ...prev, year: e.target.value }))}
                style={{ minWidth: '120px' }}
              >
                <option value="">All</option>
                {uniqueYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: '#6b7280',
                  marginBottom: '0.25rem'
                }}
              >
                Status
              </label>
              <select
                className="filter-select"
                value={studentFilters.status}
                onChange={(e) => setStudentFilters(prev => ({ ...prev, status: e.target.value }))}
                style={{ minWidth: '160px' }}
              >
                <option value="">All</option>
                <option value="Placed">Placed</option>
                <option value="Applied">Applied</option>
                <option value="Interviewing">Interviewing</option>
                <option value="Not Applied">Not Applied</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: '260px', maxWidth: '360px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#6b7280'
              }}
            >
              Search by name, email, or department
            </label>
            <input
              type="text"
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
              placeholder="e.g. Computer Science, Jane, @university.ac.ke"
              style={{
                padding: '0.7rem 0.9rem',
                borderRadius: '10px',
                border: '1px solid #e2e8f0',
                fontSize: '0.9rem',
                outline: 'none'
              }}
            />
          </div>
        </div>

        {/* Students table */}
        <div className="table-container">
          {studentsLoading && (
            <div className="empty-state">
              <div className="empty-icon">🎓</div>
              <h3>Loading students...</h3>
              <p>Please wait while we fetch the latest student list for your university.</p>
            </div>
          )}

          {!studentsLoading && studentsError && (
            <div className="empty-state">
              <div className="empty-icon">⚠️</div>
              <h3>Unable to load students</h3>
              <p>{studentsError}</p>
            </div>
          )}

          {!studentsLoading && !studentsError && filteredStudents.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">🎓</div>
              <h3>No students match your filters yet</h3>
              <p>Try adjusting the filters or clearing the search.</p>
            </div>
          )}

          {!studentsLoading && !studentsError && filteredStudents.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Email</th>
                  <th>Department</th>
                  <th>Year</th>
                  <th>Status</th>
                  <th>Applications</th>
                  <th>Scores</th>
                  <th>Skills</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((student) => (
                  <tr key={student.id}>
                    <td>
                      <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '0.1rem' }}>
                        {student.name || 'Unnamed'}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                        {student.userType === 'intern' ? 'Intern' : 'Student'}
                      </div>
                    </td>
                    <td>{student.email}</td>
                    <td>{student.department}</td>
                    <td>{student.year}</td>
                    <td>
                      <span className={getStatusClass(student.status)}>
                        {student.status || 'Not Applied'}
                      </span>
                    </td>
                    <td>{student.applications || 0}</td>
                    <td>
                      <div style={{ fontSize: '0.8rem', color: '#0f172a' }}>
                        Resume: <strong>{student.resumeScore ?? 0}</strong>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#0f172a' }}>
                        Readiness: <strong>{student.careerReadiness ?? 0}</strong>
                      </div>
                    </td>
                    <td>
                      <div className="skill-tags">
                        {(student.skills || []).slice(0, 3).map((skill, idx) => (
                          <span key={idx}>{skill}</span>
                        ))}
                        {(student.skills || []).length > 3 && (
                          <span>+{student.skills.length - 3} more</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
    </div>
  );
  };

  const renderCompanies = () => {
    const totalStudentsPlaced = companies.reduce((sum, company) => sum + (company.studentsHired || 0), 0);
    const totalActiveInternships = companies.reduce((sum, company) => sum + (company.activeInternships || 0), 0);

    return (
      <div className="section-card">
        <h2>Partner Companies</h2>
        <p style={{ marginBottom: '1.5rem', color: '#64748b', maxWidth: '720px' }}>
          Companies where your students have been placed. These are the organizations that have successfully
          hired graduates from your university through the Akshar Jobs platform.
        </p>

        {companiesError && (
          <div style={{
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            border: '1px solid rgba(248, 113, 113, 0.7)',
            background: 'rgba(248, 113, 113, 0.06)',
            color: '#991b1b',
            marginBottom: '1rem',
            fontSize: '0.9rem'
          }}>
            {companiesError}
          </div>
        )}

        {/* Summary Stats */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}
        >
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
              Total Companies
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#0f172a' }}>{companies.length}</div>
          </div>
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
              Students Placed
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#10b981' }}>{totalStudentsPlaced}</div>
          </div>
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
              Active Internships Postings
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#3b82f6' }}>{totalActiveInternships}</div>
          </div>
        </div>

        {/* Companies List */}
        {companiesLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <ThemedLoadingSpinner theme="university" />
            <p style={{ marginTop: '1rem', color: '#64748b' }}>Loading companies...</p>
          </div>
        ) : companies.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '1px dashed #e2e8f0'
          }}>
            <FontAwesomeIcon icon={faBuilding} style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '1rem' }} />
            <p style={{ fontSize: '1rem', color: '#64748b', marginBottom: '0.5rem' }}>No companies yet</p>
            <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
              Companies will appear here once your students are placed through the platform.
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.25rem' }}>
            {companies.map((company) => (
              <div
                key={company.id}
                style={{
                  padding: '1.5rem',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  background: '#ffffff',
                  transition: 'all 0.2s',
                  cursor: 'pointer'
                }}
                onClick={() => setSelectedCompany(company)}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
                  {company.logo ? (
                    <img
                      src={company.logo}
                      alt={company.name}
                      style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '8px',
                        objectFit: 'cover',
                        border: '1px solid #e2e8f0'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    style={{
                      display: company.logo ? 'none' : 'flex',
                      width: '56px',
                      height: '56px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.1) 0%, rgba(13, 148, 136, 0.1) 100%)',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      fontWeight: 700,
                      color: '#f97316'
                    }}
                  >
                    {company.name.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      color: '#0f172a',
                      margin: 0,
                      marginBottom: '0.25rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {company.name}
                    </h3>
                    {company.category && (
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.6rem',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        background: 'rgba(249, 115, 22, 0.1)',
                        color: '#f97316'
                      }}>
                        {company.category}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: '#64748b' }}>
                    <FontAwesomeIcon icon={faUsers} style={{ color: '#10b981' }} />
                    <span><strong style={{ color: '#0f172a' }}>{company.studentsHired || 0}</strong> student{company.studentsHired !== 1 ? 's' : ''} placed</span>
                  </div>
                  {company.activeInternships > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: '#64748b' }}>
                      <FontAwesomeIcon icon={faBuilding} style={{ color: '#3b82f6' }} />
                      <span><strong style={{ color: '#0f172a' }}>{company.activeInternships}</strong> active internship{company.activeInternships !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  {company.location && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#64748b' }}>
                      <FontAwesomeIcon icon={faMapMarkerAlt} style={{ fontSize: '0.75rem' }} />
                      <span>{company.location}</span>
                    </div>
                  )}
                </div>

                {company.students && company.students.length > 0 && (
                  <div style={{
                    paddingTop: '0.75rem',
                    borderTop: '1px solid #e2e8f0',
                    fontSize: '0.85rem',
                    color: '#64748b'
                  }}>
                    <div style={{ marginBottom: '0.5rem', fontWeight: 600, color: '#0f172a' }}>
                      Placed Students:
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                      {company.students.slice(0, 3).map((student, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.2) 0%, rgba(13, 148, 136, 0.2) 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: '#f97316'
                          }}>
                            {student.name.charAt(0).toUpperCase()}
                          </div>
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {student.name}
                          </span>
                          {student.department && (
                            <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                              {student.department}
                            </span>
                          )}
                        </div>
                      ))}
                      {company.students.length > 3 && (
                        <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontStyle: 'italic' }}>
                          +{company.students.length - 3} more student{company.students.length - 3 !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Company Detail Modal */}
        {selectedCompany && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
          onClick={() => setSelectedCompany(null)}
          >
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                  {selectedCompany.name}
                </h3>
                <button
                  type="button"
                  onClick={() => setSelectedCompany(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {selectedCompany.logo && (
                  <div style={{ textAlign: 'center' }}>
                    <img
                      src={selectedCompany.logo}
                      alt={selectedCompany.name}
                      style={{
                        width: '120px',
                        height: '120px',
                        borderRadius: '12px',
                        objectFit: 'cover',
                        border: '1px solid #e2e8f0'
                      }}
                    />
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem' }}>Students Placed</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#10b981' }}>
                      {selectedCompany.studentsHired || 0}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem' }}>Active Internships</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#3b82f6' }}>
                      {selectedCompany.activeInternships || 0}
                    </div>
                  </div>
                </div>

                {selectedCompany.category && (
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem' }}>Industry</div>
                    <div style={{ fontSize: '0.95rem', color: '#0f172a', fontWeight: 600 }}>{selectedCompany.category}</div>
                  </div>
                )}

                {selectedCompany.location && (
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem' }}>Location</div>
                    <div style={{ fontSize: '0.95rem', color: '#0f172a' }}>{selectedCompany.location}</div>
                  </div>
                )}

                {selectedCompany.website && (
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem' }}>Website</div>
                    <a
                      href={selectedCompany.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: '0.95rem', color: '#3b82f6', textDecoration: 'none' }}
                    >
                      {selectedCompany.website}
                    </a>
                  </div>
                )}

                {selectedCompany.students && selectedCompany.students.length > 0 && (
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.75rem', fontWeight: 600 }}>
                      Placed Students ({selectedCompany.students.length})
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '300px', overflowY: 'auto' }}>
                      {selectedCompany.students.map((student, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: '0.75rem',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            background: '#f9fafb'
                          }}
                        >
                          <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#0f172a', marginBottom: '0.25rem' }}>
                            {student.name}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                            {student.department || 'Department not specified'}
                          </div>
                          {student.email && (
                            <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                              {student.email}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleEventFormChange = (field, value) => {
    setEventForm(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateEvent = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        buildApiUrl('/api/university/events'),
        eventForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh events list
      const response = await axios.get(
        buildApiUrl('/api/university/events'),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        setEvents(response.data.events || []);
      }
      
      // Reset form
      setEventForm({
        title: '',
        description: '',
        date: '',
        time: '',
        location: '',
        type: 'Campus Drive',
        maxAttendees: '',
        organizer: '',
        contactEmail: '',
        contactPhone: ''
      });
      setShowEventForm(false);
    } catch (error) {
      console.error('Failed to create event:', error);
      alert('Failed to create event. Please try again.');
    }
  };

  const handleUpdateEvent = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        buildApiUrl(`/api/university/events/${editingEvent.id}`),
        eventForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh events list
      const response = await axios.get(
        buildApiUrl('/api/university/events'),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        setEvents(response.data.events || []);
      }
      
      // Reset form
      setEditingEvent(null);
      setEventForm({
        title: '',
        description: '',
        date: '',
        time: '',
        location: '',
        type: 'Campus Drive',
        maxAttendees: '',
        organizer: '',
        contactEmail: '',
        contactPhone: ''
      });
      setShowEventForm(false);
    } catch (error) {
      console.error('Failed to update event:', error);
      alert('Failed to update event. Please try again.');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        buildApiUrl(`/api/university/events/${eventId}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh events list
      const response = await axios.get(
        buildApiUrl('/api/university/events'),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        setEvents(response.data.events || []);
      }
    } catch (error) {
      console.error('Failed to delete event:', error);
      alert('Failed to delete event. Please try again.');
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title || '',
      description: event.description || '',
      date: event.date ? event.date.split('T')[0] : '',
      time: event.time || '',
      location: event.location || '',
      type: event.type || 'Campus Drive',
      maxAttendees: event.maxAttendees || '',
      organizer: event.organizer || '',
      contactEmail: event.contactEmail || '',
      contactPhone: event.contactPhone || ''
    });
    setShowEventForm(true);
  };

  const renderEvents = () => {
    const now = new Date();
    const filteredEvents = events.filter(event => {
      if (eventFilter === 'all') return true;
      if (!event.date) return false;
      const eventDate = new Date(event.date);
      if (eventFilter === 'upcoming') return eventDate >= now;
      if (eventFilter === 'past') return eventDate < now;
      return true;
    });

    const eventTypes = ['Campus Drive', 'Career Workshop', 'Placement Fair', 'Company Visit', 'Networking Event', 'General'];

    return (
      <div className="section-card">
        <div style={{ marginBottom: '1.5rem' }}>
          <h2>Events Management</h2>
          <p style={{ marginTop: '0.5rem', color: '#64748b', maxWidth: '720px', marginBottom: '1rem' }}>
            Manage campus recruitment events, career workshops, and placement activities for your university.
          </p>
          
          {/* All buttons in one horizontal row */}
          <div
            style={{
              display: 'flex',
              gap: '0.75rem',
              flexWrap: 'nowrap',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <button
              type="button"
              className="btn btn-primary"
              style={{
                flex: 1,
                fontSize: '0.9rem',
                padding: '0.6rem 1.2rem',
                whiteSpace: 'nowrap'
              }}
              onClick={() => {
                setEditingEvent(null);
                setEventForm({
                  title: '',
                  description: '',
                  date: '',
                  time: '',
                  location: '',
                  type: 'Campus Drive',
                  maxAttendees: '',
                  organizer: '',
                  contactEmail: '',
                  contactPhone: ''
                });
                setShowEventForm(true);
              }}
            >
              <FontAwesomeIcon icon={faCalendar} style={{ marginRight: '0.5rem' }} />
              Create New Event
            </button>
            <button
              type="button"
              onClick={() => setEventFilter('all')}
              className={eventFilter === 'all' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ flex: 1, fontSize: '0.9rem', padding: '0.6rem 1.2rem', whiteSpace: 'nowrap' }}
            >
              All Events ({events.length})
            </button>
            <button
              type="button"
              onClick={() => setEventFilter('upcoming')}
              className={eventFilter === 'upcoming' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ flex: 1, fontSize: '0.9rem', padding: '0.6rem 1.2rem', whiteSpace: 'nowrap' }}
            >
              Upcoming ({events.filter(e => e.date && new Date(e.date) >= now).length})
            </button>
            <button
              type="button"
              onClick={() => setEventFilter('past')}
              className={eventFilter === 'past' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ flex: 1, fontSize: '0.9rem', padding: '0.6rem 1.2rem', whiteSpace: 'nowrap' }}
            >
              Past ({events.filter(e => e.date && new Date(e.date) < now).length})
            </button>
          </div>
        </div>

        {eventsError && (
          <div style={{
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            border: '1px solid rgba(248, 113, 113, 0.7)',
            background: 'rgba(248, 113, 113, 0.06)',
            color: '#991b1b',
            marginBottom: '1rem',
            fontSize: '0.9rem'
          }}>
            {eventsError}
          </div>
        )}

        {/* Event Form Modal - Completely redesigned for stability */}
        {showEventForm && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2rem 1rem'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowEventForm(false);
                setEditingEvent(null);
              }
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                width: '100%',
                maxWidth: '600px',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
              }}
            >
              {/* Header - Fixed */}
              <div style={{ 
                padding: '1.5rem',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0
              }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
                  {editingEvent ? 'Edit Event' : 'Create New Event'}
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowEventForm(false);
                    setEditingEvent(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#6b7280',
                    padding: '0.25rem',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px'
                  }}
                >
                  ×
                </button>
              </div>

              {/* Scrollable Content */}
              <div style={{ 
                padding: '1.5rem',
                overflowY: 'auto',
                flex: 1,
                minHeight: 0
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                      Event Title *
                    </label>
                    <input
                      type="text"
                      value={eventForm.title}
                      onChange={(e) => handleEventFormChange('title', e.target.value)}
                      placeholder="e.g. Tech Career Fair 2024"
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                      Description
                    </label>
                    <textarea
                      value={eventForm.description}
                      onChange={(e) => handleEventFormChange('description', e.target.value)}
                      placeholder="Event description and details..."
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                        Date *
                      </label>
                      <input
                        type="date"
                        value={eventForm.date}
                        onChange={(e) => handleEventFormChange('date', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.625rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                        Time
                      </label>
                      <input
                        type="time"
                        value={eventForm.time}
                        onChange={(e) => handleEventFormChange('time', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.625rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                      Location
                    </label>
                    <input
                      type="text"
                      value={eventForm.location}
                      onChange={(e) => handleEventFormChange('location', e.target.value)}
                      placeholder="e.g. Main Auditorium, Building A"
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                        Event Type
                      </label>
                      <select
                        value={eventForm.type}
                        onChange={(e) => handleEventFormChange('type', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.625rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '0.875rem',
                          backgroundColor: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        {eventTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                        Max Attendees
                      </label>
                      <input
                        type="number"
                        value={eventForm.maxAttendees}
                        onChange={(e) => handleEventFormChange('maxAttendees', e.target.value)}
                        placeholder="0 for unlimited"
                        min="0"
                        style={{
                          width: '100%',
                          padding: '0.625rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                      Organizer
                    </label>
                    <input
                      type="text"
                      value={eventForm.organizer}
                      onChange={(e) => handleEventFormChange('organizer', e.target.value)}
                      placeholder="e.g. Placement Office"
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                        Contact Email
                      </label>
                      <input
                        type="email"
                        value={eventForm.contactEmail}
                        onChange={(e) => handleEventFormChange('contactEmail', e.target.value)}
                        placeholder="contact@university.ac.ke"
                        style={{
                          width: '100%',
                          padding: '0.625rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                        Contact Phone
                      </label>
                    <input
                      type="tel"
                      value={eventForm.contactPhone}
                      onChange={(e) => handleEventFormChange('contactPhone', e.target.value)}
                      placeholder="+254 700 000 000"
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem'
                      }}
                    />
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer - Fixed */}
              <div style={{ 
                padding: '1.5rem',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                gap: '0.75rem',
                justifyContent: 'flex-end',
                flexShrink: 0
              }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEventForm(false);
                    setEditingEvent(null);
                  }}
                  style={{ padding: '0.625rem 1.25rem' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={editingEvent ? handleUpdateEvent : handleCreateEvent}
                  disabled={!eventForm.title || !eventForm.date}
                  style={{ padding: '0.625rem 1.25rem' }}
                >
                  {editingEvent ? 'Update Event' : 'Create Event'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Events List */}
        {eventsLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <ThemedLoadingSpinner theme="university" />
            <p style={{ marginTop: '1rem', color: '#64748b' }}>Loading events...</p>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '1px dashed #e2e8f0'
          }}>
            <FontAwesomeIcon icon={faCalendar} style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '1rem' }} />
            <p style={{ fontSize: '1rem', color: '#64748b', marginBottom: '0.5rem' }}>No events found</p>
            <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
              {eventFilter === 'all' 
                ? 'Create your first event to get started.'
                : `No ${eventFilter} events. Try selecting a different filter.`}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {filteredEvents.map((event) => {
              const eventDate = event.date ? new Date(event.date) : null;
              const isUpcoming = eventDate && eventDate >= now;
              const isPast = eventDate && eventDate < now;

              return (
                <div
                  key={event.id}
                  style={{
                    padding: '1.25rem 1.5rem',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    background: isPast ? '#f9fafb' : '#ffffff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '1.5rem'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                        {event.title}
                      </h3>
                      <span style={{
                        padding: '0.25rem 0.6rem',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        background: event.type === 'Campus Drive' ? 'rgba(249, 115, 22, 0.1)' : 
                                   event.type === 'Career Workshop' ? 'rgba(59, 130, 246, 0.1)' :
                                   event.type === 'Placement Fair' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(156, 163, 175, 0.1)',
                        color: event.type === 'Campus Drive' ? '#f97316' : 
                               event.type === 'Career Workshop' ? '#3b82f6' :
                               event.type === 'Placement Fair' ? '#10b981' : '#6b7280'
                      }}>
                        {event.type}
                      </span>
                      {isUpcoming && (
                        <span style={{
                          padding: '0.25rem 0.6rem',
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          background: 'rgba(16, 185, 129, 0.1)',
                          color: '#10b981'
                        }}>
                          Upcoming
                        </span>
                      )}
                      {isPast && (
                        <span style={{
                          padding: '0.25rem 0.6rem',
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          background: 'rgba(156, 163, 175, 0.1)',
                          color: '#6b7280'
                        }}>
                          Past
                        </span>
                      )}
                    </div>
                    {event.description && (
                      <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '0.75rem', lineHeight: '1.5' }}>
                        {event.description.length > 150 ? `${event.description.substring(0, 150)}...` : event.description}
                      </p>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.85rem', color: '#64748b' }}>
                      {eventDate && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <FontAwesomeIcon icon={faCalendar} />
                          <span>{eventDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                          {event.time && <span>at {event.time}</span>}
                        </div>
                      )}
                      {event.location && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <FontAwesomeIcon icon={faBuilding} />
                          <span>{event.location}</span>
                        </div>
                      )}
                      {event.maxAttendees > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <FontAwesomeIcon icon={faUsers} />
                          <span>{event.attendees || 0} / {event.maxAttendees} attendees</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => handleEditEvent(event)}
                      style={{ fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => handleDeleteEvent(event.id)}
                      style={{ 
                        fontSize: '0.85rem', 
                        padding: '0.5rem 0.75rem',
                        background: '#fee2e2',
                        color: '#dc2626',
                        border: '1px solid #fecaca'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderAnalytics = () => {
    const stats = dashboardData.stats || {};

    // Basic placement numbers
    const total = stats.totalStudents || 0;
    const placed = stats.studentsPlaced || 0;
    const activeSeekers = stats.activeJobSeekers || 0;
    const activeInterns = stats.activeInterns || 0;
    const notPlaced = total - placed;
    const placementRate = total > 0 ? Math.round((placed / total) * 100) : 0;

    // Chart data for Overall Placement Rate (Bar Chart)
    const placementRateChartData = {
      labels: ['Placement Rate'],
      datasets: [
        {
          label: 'Placed',
          data: [placementRate],
          backgroundColor: 'rgba(16, 185, 129, 0.8)',
          borderColor: 'rgba(16, 185, 129, 1)',
          borderWidth: 2
        },
        {
          label: 'Not Placed',
          data: [100 - placementRate],
          backgroundColor: 'rgba(229, 231, 235, 0.8)',
          borderColor: 'rgba(229, 231, 235, 1)',
          borderWidth: 2
        }
      ]
    };

    const placementRateChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top'
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `${context.dataset.label}: ${context.parsed.y}%`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          ticks: {
            callback: function(value) {
              return value + '%';
            }
          }
        }
      }
    };

    // Chart data for Student Status Distribution (Doughnut Chart)
    const studentStatusChartData = {
      labels: ['Placed', 'Active Job Seekers', 'Active Interns', 'Not Active'],
      datasets: [
        {
          data: [
            placed,
            activeSeekers,
            activeInterns,
            Math.max(0, total - placed - activeSeekers - activeInterns)
          ],
          backgroundColor: [
            'rgba(16, 185, 129, 0.8)',
            'rgba(249, 115, 22, 0.8)',
            'rgba(59, 130, 246, 0.8)',
            'rgba(156, 163, 175, 0.8)'
          ],
          borderColor: [
            'rgba(16, 185, 129, 1)',
            'rgba(249, 115, 22, 1)',
            'rgba(59, 130, 246, 1)',
            'rgba(156, 163, 175, 1)'
          ],
          borderWidth: 2
        }
      ]
    };

    const studentStatusChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'right'
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const label = context.label || '';
              const value = context.parsed || 0;
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
              return `${label}: ${value} (${percentage}%)`;
            }
          }
        }
      }
    };

    // Chart data for Placement Rate Distribution (Bar Chart)
    const distributionRanges = ['0-25%', '26-50%', '51-75%', '76-100%'];
    const distributionValues = [
      placementRate <= 25 ? placementRate : 0,
      placementRate > 25 && placementRate <= 50 ? placementRate - 25 : placementRate > 50 ? 25 : 0,
      placementRate > 50 && placementRate <= 75 ? placementRate - 50 : placementRate > 75 ? 25 : 0,
      placementRate > 75 ? placementRate - 75 : 0
    ];

    const distributionChartData = {
      labels: distributionRanges,
      datasets: [
        {
          label: 'Placement Rate %',
          data: distributionValues,
          backgroundColor: [
            'rgba(59, 130, 246, 0.8)',
            'rgba(56, 189, 248, 0.8)',
            'rgba(16, 185, 129, 0.8)',
            'rgba(34, 197, 94, 0.8)'
          ],
          borderColor: [
            'rgba(59, 130, 246, 1)',
            'rgba(56, 189, 248, 1)',
            'rgba(16, 185, 129, 1)',
            'rgba(34, 197, 94, 1)'
          ],
          borderWidth: 2
        }
      ]
    };

    const distributionChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `${context.parsed.y}%`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          ticks: {
            callback: function(value) {
              return value + '%';
            }
          }
        }
      }
    };

    return (
      <div className="section-card">
        <h2>Analytics</h2>
        <p style={{ marginBottom: '1.5rem', color: '#64748b', maxWidth: '720px' }}>
          High-level analytics for your university placements. These charts use the same live data that powers the
          Students Management section and dashboard stats.
        </p>

        {/* Top metrics row (without Active Job Seekers card) */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}
        >
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
              Total Students
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#0f172a' }}>{total}</div>
          </div>
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
              Students Placed
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#10b981' }}>{placed}</div>
          </div>
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
              Active Interns
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#3b82f6' }}>{activeInterns}</div>
          </div>
        </div>

        {/* Overall Placement Rate Chart */}
        <div
          className="section-card"
          style={{
            padding: '1.5rem 1.75rem',
            marginBottom: '1.5rem'
          }}
        >
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: '#0f172a' }}>
            Overall Placement Rate
          </h3>
          {total === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
              <p style={{ fontSize: '0.9rem', margin: 0 }}>
                No placement data yet. This chart will update automatically as students are placed.
              </p>
            </div>
          ) : (
            <div style={{ height: '300px' }}>
              <Bar data={placementRateChartData} options={placementRateChartOptions} />
            </div>
          )}
        </div>

        {/* Student Status Distribution Chart */}
        <div
          className="section-card"
          style={{
            padding: '1.5rem 1.75rem',
            marginBottom: '1.5rem'
          }}
        >
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: '#0f172a' }}>
            Student Status Distribution
          </h3>
          {total === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
              <p style={{ fontSize: '0.9rem', margin: 0 }}>No student data yet.</p>
            </div>
          ) : (
            <div style={{ height: '300px' }}>
              <Doughnut data={studentStatusChartData} options={studentStatusChartOptions} />
            </div>
          )}
        </div>

        {/* Placement Rate Distribution Chart */}
        <div
          className="section-card"
          style={{
            padding: '1.5rem 1.75rem'
          }}
        >
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: '#0f172a' }}>
            Placement Rate Distribution
          </h3>
          {total === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
              <p style={{ fontSize: '0.9rem', margin: 0 }}>No placement data yet.</p>
            </div>
          ) : (
            <div style={{ height: '300px' }}>
              <Bar data={distributionChartData} options={distributionChartOptions} />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderBulkUpload = () => (
    <div className="section-card">
      <h2>Bulk Upload Students</h2>
      <p style={{ marginBottom: '1.5rem', color: '#64748b', maxWidth: '720px' }}>
        Upload student cohorts in bulk using a CSV or Excel file. Download the template, fill in your cohort details,
        and upload here. Students will be created immediately and visible in the Students section. Super Admin can send welcome emails from the bulk-import dashboard if needed.
      </p>

      <div
        style={{
          padding: '1.5rem 1.75rem',
          borderRadius: '16px',
          border: '1px dashed rgba(148, 163, 184, 0.9)',
          background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.03) 0%, rgba(13, 148, 136, 0.03) 100%)',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.9rem'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', marginBottom: '0.2rem' }}>
              Bulk upload new students
            </div>
            <div style={{ fontSize: '0.85rem', color: '#64748b', maxWidth: '500px' }}>
              Download the CSV template, fill in your cohort details, and upload here. Students will be created immediately and visible in the Students section.
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: '220px' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleDownloadStudentTemplate}
            >
              Download Student Template (CSV)
            </button>
            <input
              id="bulk-students-file"
              type="file"
              accept=".csv,.xls,.xlsx"
              onChange={handleBulkStudentsUpload}
              disabled={bulkUploading}
            />
            {bulkUploading && (
              <span style={{ fontSize: '0.85rem', color: '#0d9488', fontWeight: 600 }}>
                Uploading student file...
              </span>
            )}
          </div>
        </div>

        {bulkUploadResult && (
          <div
            style={{
              marginTop: '0.25rem',
              padding: '0.75rem 1rem',
              borderRadius: '12px',
              border: `1px solid ${
                bulkUploadResult.type === 'success' ? 'rgba(16, 185, 129, 0.6)' : 'rgba(248, 113, 113, 0.7)'
              }`,
              background:
                bulkUploadResult.type === 'success'
                  ? 'rgba(16, 185, 129, 0.06)'
                  : 'rgba(248, 113, 113, 0.06)',
              color: bulkUploadResult.type === 'success' ? '#065f46' : '#991b1b',
              fontSize: '0.9rem',
              fontWeight: 500
            }}
          >
            {bulkUploadResult.message}
          </div>
        )}
      </div>
    </div>
  );

  const handleToggleNotification = (key) => {
    setNotificationSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleTogglePrivacy = (key) => {
    setPrivacySettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleDisplayChange = (field, value) => {
    setDisplaySettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setSettingsSaving(true);
      setSettingsError('');
      setSettingsSuccess('');

      const token = localStorage.getItem('token');
      if (!token) {
        setSettingsError('You need to be logged in to update settings.');
        setSettingsSaving(false);
        return;
      }

      const response = await axios.put(
        buildApiUrl('/api/user/settings'),
        {
          notifications: notificationSettings,
          privacy: privacySettings,
          display: displaySettings
        },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );

      if (response.data && response.data.message) {
        setSettingsSuccess(response.data.message || 'Settings saved successfully.');
      } else {
        setSettingsSuccess('Settings saved successfully.');
      }

      // Auto-clear success message after 5 seconds
      setTimeout(() => {
        setSettingsSuccess('');
      }, 5000);
    } catch (error) {
      console.error('Failed to save university settings:', error);
      const errorMessage = error.response?.data?.error || 
                          error.message || 
                          'Failed to save settings. Please try again.';
      setSettingsError(errorMessage);
      
      // Auto-clear error message after 8 seconds
      setTimeout(() => {
        setSettingsError('');
      }, 8000);
    } finally {
      setSettingsSaving(false);
    }
  };

  const renderSettings = () => (
    <div className="section-card">
      <h2>University Settings</h2>
      <p style={{ marginBottom: '1.5rem', color: '#64748b', maxWidth: '720px' }}>
        Configure how your university uses Akshar Jobs. These settings control placement notifications,
        how employers can interact with your students, and basic display preferences for your team.
      </p>

      {settingsLoading && (
        <div style={{ 
          marginBottom: '1rem', 
          padding: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          fontSize: '0.9rem', 
          color: '#64748b',
          background: 'rgba(249, 115, 22, 0.05)',
          borderRadius: '8px',
          border: '1px solid rgba(249, 115, 22, 0.2)'
        }}>
          <ThemedLoadingSpinner theme="university" />
          <span>Loading current settings...</span>
        </div>
      )}
      {settingsError && (
        <div
          style={{
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            border: '1px solid rgba(248, 113, 113, 0.7)',
            background: 'rgba(248, 113, 113, 0.06)',
            color: '#991b1b',
            fontSize: '0.9rem'
          }}
        >
          {settingsError}
        </div>
      )}
      {settingsSuccess && (
        <div
          style={{
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            border: '1px solid rgba(16, 185, 129, 0.7)',
            background: 'rgba(16, 185, 129, 0.06)',
            color: '#065f46',
            fontSize: '0.9rem'
          }}
        >
          {settingsSuccess}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)',
          gap: '1.5rem'
        }}
      >
        {/* Left column: Notifications + Privacy */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div
            className="section-card"
            style={{ padding: '1.25rem 1.5rem', marginBottom: 0 }}
          >
            <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.75rem', color: '#0f172a' }}>
              Placement & Notification Preferences
            </h3>
            <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.75rem' }}>
              Control which emails and alerts your placement office receives.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                fontSize: '0.9rem', 
                color: '#0f172a',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '6px'
              }}>
                <span style={{ flex: 1, marginRight: '1rem' }}>
                  Email notifications for new employer job postings targeting your university
                </span>
                <input
                  type="checkbox"
                  checked={!!notificationSettings.emailNotifications}
                  onChange={() => handleToggleNotification('emailNotifications')}
                  disabled={settingsSaving || settingsLoading}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                    accentColor: '#f97316',
                    opacity: (settingsSaving || settingsLoading) ? 0.6 : 1
                  }}
                />
              </label>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                fontSize: '0.9rem', 
                color: '#0f172a',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '6px'
              }}>
                <span style={{ flex: 1, marginRight: '1rem' }}>
                  Daily summary of student placement activity (offers, interviews, rejections)
                </span>
                <input
                  type="checkbox"
                  checked={!!notificationSettings.placementAlerts}
                  onChange={() => handleToggleNotification('placementAlerts')}
                  disabled={settingsSaving || settingsLoading}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                    accentColor: '#f97316',
                    opacity: (settingsSaving || settingsLoading) ? 0.6 : 1
                  }}
                />
              </label>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                fontSize: '0.9rem', 
                color: '#0f172a',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '6px'
              }}>
                <span style={{ flex: 1, marginRight: '1rem' }}>
                  Email when a bulk student upload has been processed by Super Admin
                </span>
                <input
                  type="checkbox"
                  checked={!!notificationSettings.bulkUploadNotifications}
                  onChange={() => handleToggleNotification('bulkUploadNotifications')}
                  disabled={settingsSaving || settingsLoading}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                    accentColor: '#f97316',
                    opacity: (settingsSaving || settingsLoading) ? 0.6 : 1
                  }}
                />
              </label>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                fontSize: '0.9rem', 
                color: '#0f172a',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '6px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(249, 115, 22, 0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <span style={{ flex: 1, marginRight: '1rem' }}>
                  Reminders for upcoming campus events, drives, and career workshops
                </span>
                <input
                  type="checkbox"
                  checked={!!notificationSettings.eventReminders}
                  onChange={() => handleToggleNotification('eventReminders')}
                  disabled={settingsSaving || settingsLoading}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                    accentColor: '#f97316',
                    opacity: (settingsSaving || settingsLoading) ? 0.6 : 1
                  }}
                />
              </label>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                fontSize: '0.9rem', 
                color: '#0f172a',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '6px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(249, 115, 22, 0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <span style={{ flex: 1, marginRight: '1rem' }}>Weekly digest email with key stats and trends</span>
                <input
                  type="checkbox"
                  checked={!!notificationSettings.weeklyDigest}
                  onChange={() => handleToggleNotification('weeklyDigest')}
                  disabled={settingsSaving || settingsLoading}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                    accentColor: '#f97316',
                    opacity: (settingsSaving || settingsLoading) ? 0.6 : 1
                  }}
                />
              </label>
            </div>
          </div>

          <div
            className="section-card"
            style={{ padding: '1.25rem 1.5rem', marginBottom: 0 }}
          >
            <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.75rem', color: '#0f172a' }}>
              Employer & Data Access
            </h3>
            <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.75rem' }}>
              Decide how much access employers and Akshar Jobs admins have to your student data.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                fontSize: '0.9rem', 
                color: '#0f172a',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '6px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(249, 115, 22, 0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <span style={{ flex: 1, marginRight: '1rem' }}>Allow verified employers to view student profiles shared by your placement team</span>
                <input
                  type="checkbox"
                  checked={!!privacySettings.shareStudentProfilesWithEmployers}
                  onChange={() => handleTogglePrivacy('shareStudentProfilesWithEmployers')}
                  disabled={settingsSaving || settingsLoading}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                    accentColor: '#f97316',
                    opacity: (settingsSaving || settingsLoading) ? 0.6 : 1
                  }}
                />
              </label>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                fontSize: '0.9rem', 
                color: '#0f172a',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '6px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(249, 115, 22, 0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <span style={{ flex: 1, marginRight: '1rem' }}>Allow external company accounts to download shared CVs</span>
                <input
                  type="checkbox"
                  checked={!!privacySettings.allowExternalCompanyAccess}
                  onChange={() => handleTogglePrivacy('allowExternalCompanyAccess')}
                  disabled={settingsSaving || settingsLoading}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                    accentColor: '#f97316',
                    opacity: (settingsSaving || settingsLoading) ? 0.6 : 1
                  }}
                />
              </label>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                fontSize: '0.9rem', 
                color: '#0f172a',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '6px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(249, 115, 22, 0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <span style={{ flex: 1, marginRight: '1rem' }}>Share anonymised placement analytics with Akshar Jobs Super Admin</span>
                <input
                  type="checkbox"
                  checked={!!privacySettings.shareAnalyticsWithAdmin}
                  onChange={() => handleTogglePrivacy('shareAnalyticsWithAdmin')}
                  disabled={settingsSaving || settingsLoading}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                    accentColor: '#f97316',
                    opacity: (settingsSaving || settingsLoading) ? 0.6 : 1
                  }}
                />
              </label>
            </div>
          </div>
        </div>

        {/* Right column: Display / Localization */}
        <div
          className="section-card"
          style={{ padding: '1.25rem 1.5rem', alignSelf: 'flex-start' }}
        >
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.75rem', color: '#0f172a' }}>
            Display & Localization
          </h3>
          <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.75rem' }}>
            Set defaults for language and time zone used across your university dashboard.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            <div>
              <label
                htmlFor="university-theme"
                style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.25rem', color: '#0f172a' }}
              >
                Theme
              </label>
              <select
                id="university-theme"
                value={displaySettings.theme}
                onChange={(e) => handleDisplayChange('theme', e.target.value)}
                disabled={settingsSaving || settingsLoading}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.6rem',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  fontSize: '0.9rem',
                  cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                  opacity: (settingsSaving || settingsLoading) ? 0.6 : 1,
                  backgroundColor: (settingsSaving || settingsLoading) ? '#f3f4f6' : '#ffffff'
                }}
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="system">System default</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="university-language"
                style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.25rem', color: '#0f172a' }}
              >
                Language
              </label>
              <select
                id="university-language"
                value={displaySettings.language}
                onChange={(e) => handleDisplayChange('language', e.target.value)}
                disabled={settingsSaving || settingsLoading}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.6rem',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  fontSize: '0.9rem',
                  cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                  opacity: (settingsSaving || settingsLoading) ? 0.6 : 1,
                  backgroundColor: (settingsSaving || settingsLoading) ? '#f3f4f6' : '#ffffff'
                }}
              >
                <option value="en">English</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="university-timezone"
                style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.25rem', color: '#0f172a' }}
              >
                Time zone
              </label>
              <select
                id="university-timezone"
                value={displaySettings.timezone}
                onChange={(e) => handleDisplayChange('timezone', e.target.value)}
                disabled={settingsSaving || settingsLoading}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.6rem',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  fontSize: '0.9rem',
                  cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                  opacity: (settingsSaving || settingsLoading) ? 0.6 : 1,
                  backgroundColor: (settingsSaving || settingsLoading) ? '#f3f4f6' : '#ffffff'
                }}
              >
                <option value="Africa/Nairobi">Africa / Nairobi (EAT)</option>
                <option value="UTC">UTC</option>
                <option value="Asia/Kolkata">Asia / Kolkata (IST)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: '1.5rem',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.75rem'
        }}
      >
        <button
          type="button"
          className="btn btn-secondary"
          disabled={settingsSaving}
          onClick={() => {
            // Reset messages, but keep current values
            setSettingsError('');
            setSettingsSuccess('');
          }}
        >
          Clear Messages
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSaveSettings}
          disabled={settingsSaving}
        >
          {settingsSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="university-dashboard-container">
        <div className="loading-container">
          <ThemedLoadingSpinner theme="university" />
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* University Dashboard Header - Full Width */}
      <UniversityDashboardHeader 
        currentPage={getSectionTitle()}
        onSearch={(term) => console.log('Search:', term)}
        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
      />

      <div className="dashboard-container university-dashboard-container" style={{ marginTop: '0' }}>
        {/* Sidebar */}
        <div 
          className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`} 
          style={{ top: '80px', height: 'calc(100vh - 80px)', paddingTop: '20px' }}
          onClick={(e) => {
            if (window.innerWidth <= 768 && e.target.closest('.nav-item')) {
              setSidebarOpen(false);
            }
          }}
        >
          <div className="nav-menu">
            {navigationItems.map((item) => (
              <div 
                key={item.id}
                className={`nav-item ${activeSection === item.section ? 'active' : ''}`}
                onClick={() => handleNavClick(item)}
              >
                <FontAwesomeIcon icon={item.icon} />
                <span>{item.label}</span>
                {item.badge && (
                  <span className="badge">
                    {item.badge}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Profile Completion Card */}
          <div className="sidebar-card" style={{ margin: '20px', padding: '16px', borderRadius: '12px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)' }}>
            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: '#fff' }}>
              Profile Complete
            </div>
            <div style={{ fontSize: '30px', fontWeight: 700, color: '#fff' }}>
              {Math.max(0, Math.min(100, dashboardData.profileCompletion || 0))}%
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="main-content">
          <div className="content-area">
            {/* Render sections based on activeSection */}
            {activeSection === 'dashboard' && renderDashboard()}
            {activeSection === 'students' && renderStudents()}
            {activeSection === 'bulk-upload' && renderBulkUpload()}
            {activeSection === 'companies' && renderCompanies()}
            {activeSection === 'events' && renderEvents()}
            {activeSection === 'analytics' && renderAnalytics()}
            {activeSection === 'settings' && renderSettings()}
          </div>
        </div>
      </div>
    </>
  );
};

export default UniversityDashboard;
